import { Injectable, inject, signal, effect, DestroyRef } from '@angular/core';

import { ViewerCoreService } from './viewer-core.service.js';
import { onViewerEvents } from './event-dispatcher-bridge';

/**
 * Manages visual appearance settings with Angular signals.
 */
@Injectable({ providedIn: 'root' })
export class AppearanceService {
  private core = inject(ViewerCoreService);
  private destroyRef = inject(DestroyRef);

  readonly fov = signal(60);
  readonly pointBudget = signal(1_000_000);
  readonly edlEnabled = signal(false);
  readonly edlRadius = signal(1.4);
  readonly edlStrength = signal(1.0);
  readonly edlOpacity = signal(1.0);
  readonly background = signal('skybox');
  readonly quality = signal('standard');
  readonly minNodeSize = signal(0);

  private _syncing = false;

  constructor() {
    // When the viewer becomes available, sync initial state and register listeners
    effect(() => {
      const viewer = this.core.viewer();
      if (!viewer) return;

      // Sync initial values from viewer
      this._syncFromViewer(viewer);

      // Register for viewer change events with lifecycle cleanup
      onViewerEvents(
        viewer,
        {
          background_changed: () => this._pullBackground(viewer),
          minnodesize_changed: () => this._pullMinNodeSize(viewer),
        },
        this.destroyRef,
      );
    });

    // Effects that push signal changes to the viewer
    effect(() => {
      const fov = this.fov();
      this._pushToViewer(v => v.setFOV?.(fov));
    });

    effect(() => {
      const budget = this.pointBudget();
      this._pushToViewer(v => v.setPointBudget?.(budget));
    });

    effect(() => {
      const enabled = this.edlEnabled();
      this._pushToViewer(v => v.setEDLEnabled?.(enabled));
    });

    effect(() => {
      const radius = this.edlRadius();
      this._pushToViewer(v => v.setEDLRadius?.(radius));
    });

    effect(() => {
      const strength = this.edlStrength();
      this._pushToViewer(v => v.setEDLStrength?.(strength));
    });

    effect(() => {
      const opacity = this.edlOpacity();
      this._pushToViewer(v => v.setEDLOpacity?.(opacity));
    });

    effect(() => {
      const bg = this.background();
      this._pushToViewer(v => v.setBackground?.(bg));
    });

    effect(() => {
      const q = this.quality();
      this._pushToViewer(v => v.setQuality?.(q));
    });

    effect(() => {
      const size = this.minNodeSize();
      this._pushToViewer(v => v.setMinNodeSize?.(size));
    });
  }

  // ─── Static options ─────────────────────────────────────────────────────────

  getBackgroundOptions(): { value: string; label: string }[] {
    return [
      { value: 'skybox', label: 'Skybox' },
      { value: 'gradient', label: 'Gradient' },
      { value: 'black', label: 'Black' },
      { value: 'white', label: 'White' },
      { value: 'null', label: 'None' },
    ];
  }

  getQualityOptions(): { value: string; label: string }[] {
    return [
      { value: 'standard', label: 'Standard' },
      { value: 'hq', label: 'High Quality' },
    ];
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private _syncFromViewer(viewer: any): void {
    this._syncing = true;
    if (viewer.fov !== undefined) this.fov.set(viewer.fov);
    if (typeof viewer.getPointBudget === 'function')
      this.pointBudget.set(viewer.getPointBudget() || 1_000_000);
    if (typeof viewer.getEDLEnabled === 'function')
      this.edlEnabled.set(viewer.getEDLEnabled() || false);
    if (typeof viewer.getEDLRadius === 'function') this.edlRadius.set(viewer.getEDLRadius() || 1.4);
    if (typeof viewer.getEDLStrength === 'function')
      this.edlStrength.set(viewer.getEDLStrength() || 1.0);
    if (typeof viewer.getEDLOpacity === 'function')
      this.edlOpacity.set(viewer.getEDLOpacity() || 1.0);
    if (typeof viewer.getBackground === 'function')
      this.background.set(viewer.getBackground() || 'skybox');
    if (typeof viewer.getQuality === 'function')
      this.quality.set(viewer.getQuality() || 'standard');
    if (typeof viewer.getMinNodeSize === 'function')
      this.minNodeSize.set(viewer.getMinNodeSize() || 0);
    this._syncing = false;
  }

  private _pullBackground(viewer: any): void {
    this._syncing = true;
    this.background.set(viewer.getBackground?.() || 'skybox');
    this._syncing = false;
  }

  private _pullMinNodeSize(viewer: any): void {
    this._syncing = true;
    this.minNodeSize.set(viewer.getMinNodeSize?.() || 0);
    this._syncing = false;
  }

  private _pushToViewer(fn: (viewer: any) => void): void {
    if (this._syncing) return;
    const viewer = this.core.viewer();
    if (viewer) fn(viewer);
  }
}
